
import { computed, defineComponent, Ref, ref, watch } from 'vue';

export default defineComponent({
    name: 'ConfirmationButton',
    props: {
        text: {
            type: String,
            required: true,
        },
        confirmationText: {
            type: String,
            required: true,
        },
        confirm: {
            type: Boolean,
            required: true,
        },
        confirmMinDuration: {
            type: Number,
            required: false,
            default: 1000,
        },
        disabled: {
            default: false,
            required: false,
            type: Boolean,
        },
        animationDuration: {
            type: Number,
            required: false,
            default: 300,
            validator: (value: number) => value % 100 === 0 && value < 1100,
        },
    },
    setup(props) {
        const internalPending: Ref<boolean> = ref(false);
        let internalPendingTimer;
        let externalPendingTimer;

        const externalPending = computed(() => props.confirm);
        const isPending = computed(() => externalPending.value || internalPending.value);

        const clearInternalPending = (): void => {
            internalPendingTimer && window.clearTimeout(internalPendingTimer as number);
            internalPendingTimer = null;
        };

        const clearExternalPending = (): void => {
            externalPendingTimer && window.clearTimeout(externalPendingTimer as number);
            externalPendingTimer = null;
        };

        const setInternalPending = (externalPendingValue: boolean): void => {
            if (externalPendingValue) {
                clearExternalPending();
                externalPendingTimer = setTimeout(() => {
                    clearInternalPending();
                    internalPending.value = true;

                    internalPendingTimer = window.setTimeout(() => {
                        internalPending.value = false;
                    }, props.confirmMinDuration);
                }, 50);
            }
        };

        const enterAnimation = computed(() => {
            const animation = isPending.value ? 'fadeInDown' : 'fadeInUp';
            return `${animation} animated u-anim-dur-${props.animationDuration}`;
        });

        const leaveAnimation = computed(() => {
            const animation = isPending.value ? 'fadeOutDown' : 'fadeOutUp';
            return `${animation} animated u-anim-dur-${props.animationDuration}`;
        });

        watch(externalPending, setInternalPending, { immediate: true });

        return {
            enterAnimation,
            leaveAnimation,
            isPending,
        };
    },
});
